import React, { useState } from "react";
import {
    getDisplayErrorMessage,
    getDisplayLoadingAnimation,
    getStyleOfPaymentIframe,
    getStyleOfPaymentIframeContainer,
} from "./helpers";
import {
    useListenToPaymentIframe,
    usePollPaymentIframeForHandshake,
} from "../../hooks";
import ErrorMessage from "../ErrorMessage";
import LoadingAnimation from "./LoadingAnimation";
import "./PaymentIframe.scss";

/**
 *
 * @param {object} props
 * @param {string} props.defaultHeightOfPaymentIframe
 * @param {string} props.idOfPaymentIframe
 * @param {string} props.idOfPaymentIframeContainer
 * @param {string} props.originOfPaymentIframe
 * @param {string} props.urlOfPaymentIframe
 * @returns {React.ReactElement}
 */
const PaymentIframe = ({
    defaultHeightOfPaymentIframe,
    idOfPaymentIframe,
    idOfPaymentIframeContainer,
    originOfPaymentIframe,
    urlOfPaymentIframe,
}) => {
    const [
        hasReceivedHandshakeFromPaymentIframe,
        setHasReceivedHandshakeFromPaymentIframe,
    ] = useState(false);
    const [
        hasExceededNumberOfHandshakeAttempts,
        setHasExceededNumberOfHandshakeAttempts,
    ] = useState(false);

    // Send handshake and listen for any post messages from payment iframe
    usePollPaymentIframeForHandshake({
        hasReceivedHandshakeFromPaymentIframe,
        setHasExceededNumberOfHandshakeAttempts,
        urlOfPaymentIframe,
    });

    useListenToPaymentIframe({
        hasReceivedHandshakeFromPaymentIframe,
        originOfPaymentIframe,
        setHasReceivedHandshakeFromPaymentIframe,
    });

    // Generate flags and inline styles for the payment iframe and its container
    const displayLoadingAnimation = getDisplayLoadingAnimation(
        hasReceivedHandshakeFromPaymentIframe,
        hasExceededNumberOfHandshakeAttempts,
    );
    const displayErrorMessage = getDisplayErrorMessage(
        hasReceivedHandshakeFromPaymentIframe,
        hasExceededNumberOfHandshakeAttempts,
    );
    const styleOfPaymentIframe = getStyleOfPaymentIframe(
        hasReceivedHandshakeFromPaymentIframe,
    );
    const styleOfPaymentIframeContainer = getStyleOfPaymentIframeContainer(
        defaultHeightOfPaymentIframe,
    );

    // A few notes about the markup used here:
    // 1. The id of the the containing div is used by the resize logic as any
    // attempts to target the iframe directly result in 3px of additional white
    // space being added in between these two elements.
    // 2. Because we pay for our Source Domain implementation per domain
    // the PaymentIframe should only ever be rendered using a Qgiv domain.
    // 3. The id of the payment iframe needs to match the element we use to
    // create the post message.
    // 4. All loading animation, error message and iframe markup should all be
    // rendered within the container to ensure that it is sized correctly.
    return (
        <div
            className="-callout -padding--0"
            id={idOfPaymentIframeContainer}
            style={styleOfPaymentIframeContainer}
        >
            {displayLoadingAnimation && <LoadingAnimation />}
            {displayErrorMessage && (
                <ErrorMessage
                    additionalText="If you are unable to use one of the available payment methods please contact the organization for support."
                    dark
                    headingText="We're sorry, it looks like something went wrong when trying to load the credit card fields."
                />
            )}
            <iframe
                id={idOfPaymentIframe}
                src={urlOfPaymentIframe}
                style={styleOfPaymentIframe}
                title="qgiv-payment-iframe"
            />
        </div>
    );
};

export default PaymentIframe;
